import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { LoginPage } from "./features/auth/presentation/pages/LoginPage";
import { PasswordChangePage } from "./features/auth/presentation/pages/PasswordChangePage";
import AdminDashboard from "./features/admin/presentation/pages/AdminDashboard";
import { CompanySelectionPage } from "./features/admin/presentation/pages/CompanySelectionPage";
import { CompanyCreatePage } from "./features/admin/presentation/pages/CompanyCreatePage";
import { CompanyDetailPage } from "./features/admin/presentation/pages/CompanyDetailPage";
import { UserCreatePage } from "./features/admin/presentation/pages/UserCreatePage";
import { UserUpdatePage } from "./features/admin/presentation/pages/UserUpdatePage";
import { ComplianceTypesPage } from "./features/compliance/presentation/pages/master/ComplianceTypesPage";
import { ComplianceCalendarPage } from "./features/compliance/presentation/pages/master/ComplianceCalendarPage";
import { useAuth } from "./features/auth/presentation/hooks/useAuth";

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requireMasterAdmin?: boolean;
}> = ({ children, requireMasterAdmin = false }) => {
  const { isAuthenticated, loading, isMasterAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireMasterAdmin && !isMasterAdmin()) {
    return <Navigate to="/admin/companies/select" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  const { logout } = useAuth();

  const handleAdminLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Default route - always show login */}
          <Route path="/" element={<LoginPage />} />

          {/* Password Change - Required for first-time login */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <PasswordChangePage />
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard - Master Admin Only */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <AdminDashboard onLogout={handleAdminLogout} />
              </ProtectedRoute>
            }
          />

          {/* Company Selection - Regular Users */}
          <Route
            path="/admin/companies/select"
            element={
              <ProtectedRoute>
                <CompanySelectionPage />
              </ProtectedRoute>
            }
          />

          {/* Company Management - Master Admin Only */}
          <Route
            path="/admin/companies/create"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <CompanyCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <CompanyDetailPage />
              </ProtectedRoute>
            }
          />

          {/* User Management - Master Admin Only */}
          <Route
            path="/admin/users/create"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <UserCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/update/:id"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <UserUpdatePage />
              </ProtectedRoute>
            }
          />

          {/* Compliance Management - Master Admin Only */}
          <Route
            path="/admin/compliance/types"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <ComplianceTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/compliance/calendar"
            element={
              <ProtectedRoute requireMasterAdmin={true}>
                <ComplianceCalendarPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
